import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {
  Form,
  Dropdown,
  Table,
  Button,
  Icon,
  Modal,
  Pagination,
  Tab,
  Input,
  Header,
  Checkbox,
} from 'semantic-ui-react';
import {useReport} from '../../../providers/ReportProvider';

const GenericPointTable = ({points, pointPages, getPoints, onRowClick, selectedPoints}) => {
  useEffect(() => {
    getPoints(1);
  }, [getPoints]);
  const handlePageChange = useCallback(
    (e, data) => {
      getPoints(data.activePage);
    },
    [getPoints]
  );

  return (
    <div style={{overflowX: 'scroll'}}>
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing>Number</Table.HeaderCell>
            <Table.HeaderCell collapsing>Code</Table.HeaderCell>
            <Table.HeaderCell collapsing>Haystack Tag</Table.HeaderCell>
            <Table.HeaderCell collapsing>Brick</Table.HeaderCell>
            <Table.HeaderCell collapsing>Description</Table.HeaderCell>
            <Table.HeaderCell collapsing>Units</Table.HeaderCell>
            <Table.HeaderCell collapsing>DataType</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {(points ?? []).map(p => (
            <Table.Row
              key={p.id}
              active={selectedPoints.includes(p.id)}
              onClick={() => onRowClick(p)}
              style={{cursor: 'pointer'}}
            >
              <Table.Cell collapsing>{p.id}</Table.Cell>
              <Table.Cell collapsing>{p.code}</Table.Cell>
              <Table.Cell collapsing>{p.haystackTag}</Table.Cell>
              <Table.Cell collapsing>{p.brick}</Table.Cell>
              <Table.Cell collapsing>{p.description}</Table.Cell>
              <Table.Cell collapsing>{p.units}</Table.Cell>
              <Table.Cell collapsing>{p.dataType}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan="8">
              <Pagination
                boundaryRange={0}
                defaultActivePage={1}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                siblingRange={3}
                totalPages={Math.max(1, pointPages ?? 0)}
                onPageChange={handlePageChange}
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </div>
  );
};

const FunctionsTable = ({
    functions = [],            // full data set
    selectedFunctions = [],     // array of selected functionIds
    onRowClick,                 // optional row click handler
    itemsPerPage = 10           // page size
}) => {
    const [activePage, setActivePage] = useState(1);
    const [nameFilter, setNameFilter] = useState('');
    const [nameSortDirection, setNameSortDirection] = useState('ascending'); // 'ascending' | 'descending'

    // 1) Filter by name (case-insensitive, substring match)
    const filtered = useMemo(() => {
        const q = nameFilter.trim().toLowerCase();
        if (!q) return functions;
        return functions.filter(f => (f.name || '').toLowerCase().includes(q));
    }, [functions, nameFilter]);

    // 2) Sort by name (toggle ascending/descending)
    const sorted = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => {
            const aName = a.name || '';
            const bName = b.name || '';
            const cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base' });
            return nameSortDirection === 'ascending' ? cmp : -cmp;
        });
        return arr;
    }, [filtered, nameSortDirection]);

    // 3) Pagination over filtered + sorted results
    const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
    const safeActivePage = Math.min(activePage, totalPages);
    const start = (safeActivePage - 1) * itemsPerPage;
    const pagedFunctions = sorted.slice(start, start + itemsPerPage);

    const handleNameHeaderClick = () => {
        setNameSortDirection(prev => (prev === 'ascending' ? 'descending' : 'ascending'));
        setActivePage(1); // reset to first page when changing sort
    };

    const handleFilterChange = (e, { value }) => {
        setNameFilter(value);
        setActivePage(1); // reset to first page when filtering
    };

    const handlePageChange = (e, data) => {
        setActivePage(Number(data.activePage));
    };

    return (
        <div>
            <div style={{ marginBottom: 8 }}>
                <Input
                    icon="search"
                    placeholder="Filter by name..."
                    value={nameFilter}
                    onChange={handleFilterChange}
                />
            </div>

            <div style={{ overflowX: 'scroll' }}>
                <Table celled striped sortable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell
                                sorted={nameSortDirection}
                                onClick={handleNameHeaderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                Name
                            </Table.HeaderCell>
                            <Table.HeaderCell>Description</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {pagedFunctions.map(f => (
                            <Table.Row
                                key={f.id}
                                active={selectedFunctions.includes(f.functionId)}
                                onClick={() => onRowClick && onRowClick(f)}
                                style={{ cursor: onRowClick ? 'pointer' : undefined }}
                            >
                                <Table.Cell>{f.name}</Table.Cell>
                                <Table.Cell>{f.description}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>

                    <Table.Footer>
                        <Table.Row>
                            <Table.HeaderCell colSpan="10">
                                <Pagination
                                    boundaryRange={0}
                                    activePage={safeActivePage}
                                    ellipsisItem={null}
                                    firstItem={null}
                                    lastItem={null}
                                    siblingRange={3}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Footer>
                </Table>
            </div>
        </div>
    );
}


const MetricModal = ({open, setOpen, templateId, metricId, type}) => {
  const {
    reportMetric,
    getReportMetric,
    clearReportMetric,
    energyPoints,
    energyPointPages,
    getEnergyPoints,
    variablePoints,
    variablePointPages,
    getVariablePoints,
    positionPoints,
    positionPointPages,
    getPositionPoints,
    binaryPoints,
    binaryPointPages,
    getBinaryPoints,
    numericPoints,
    numericPointPages,
    getNumericPoints,
    functionList,
    pointList,
    upsertReportTemplateMetric,
    functionTableList,
  } = useReport();
  const [formName, setFormName] = useState('');
  const [formIsEnergyMetric, setFormIsEnergyMetric] = useState('');
  const [formComparisonIsFunction, setFormComparisonIsFunction] = useState('');
  const [formUnit, setFormUnit] = useState('');
  const [formPoints, setFormPoints] = useState([]);
  const [formFunctions, setFormFunctions] = useState([]);
  const [formPointDisplays, setFormPointDisplays] = useState([]);
  const [formFunctionDisplays, setFormFunctionDisplays] = useState([]);
  const [formIdealCurveFunction, setFormIdealCurveFunction] = useState('');
  const [formComparisonCode, setFormComparisonCode] = useState('');
  const [formElectricConsumptionFunction, setFormElectricConsumptionFunction] = useState('');
  const [formElectricConsumptionUnit, setFormElectricConsumptionUnit] = useState('');
  const [formElectricWasteFunction, setFormElectricWasteFunction] = useState('');
  const [formElectricWasteUnit, setFormElectricWasteUnit] = useState('');
  const [formThermalConsumptionFunction, setFormThermalConsumptionFunction] = useState('');
  const [formThermalConsumptionUnit, setFormThermalConsumptionUnit] = useState('');
  const [formThermalWasteFunction, setFormThermalWasteFunction] = useState('');
  const [formThermalWasteUnit, setFormThermalWasteUnit] = useState('');

  const units = [
    {key: 'kw', value: 'kW', text: 'kW'},
    {key: 'kWh', value: 'kWh', text: 'kWh'},
    {key: 'F', value: 'F', text: 'degrees F'},
    {key: 'in H20', value: 'in H20', text: 'in H20'},
    {key: 'psig', value: 'psig', text: 'psig'},
    {key: 'cfm', value: 'cfm', text: 'cfm'},
    {key: 'lb/hr', value: 'lb/hr', text: 'lb/hr'},
    {key: 'gpm', value: 'gpm', text: 'gpm'},
    {key: 'cf/hr', value: 'cf/hr', text: 'cf/hr'},
    {key: 'gal', value: 'gal', text: 'gal'},
    {key: 'hz', value: 'hz', text: 'hz'},
    {key: 'rpm', value: 'rpm', text: 'rpm'},
    {key: '%', value: '%', text: '%'},
    {key: 'Btu/lb', value: 'Btu/lb', text: 'Btu/lb'},
    {key: 'Btu', value: 'Btu', text: 'Btu'},
    {key: '$', value: '$', text: '$'},
    {key: 'kw/ton', value: 'kw/ton', text: 'kw/ton'},
    {key: 'lb/hr/ton', value: 'lb/hr/ton', text: 'lb/hr/ton'},
    {key: 'Btu/ton', value: 'Btu/ton', text: 'Btu/ton'},
    {key: 'fthd', value: 'fthd', text: 'fthd'},
    {key: 'hp', value: 'hp', text: 'hp'},
    {key: 'Btu/cfmd', value: 'Btu/cfmd', text: 'Btu/cfmd'},
    {key: 'kwh/cfmd', value: 'kwh/cfmd', text: 'kwh/cfmd'},
    {key: 'cfm/cfmd', value: 'cfm/cfmd', text: 'cfm/cfmd'},
    {key: 'cfmd', value: 'cfmd', text: 'cfmd'},
    {key: 'Btud', value: 'Btud', text: 'Btud'},
    {key: 'Btu/Btud', value: 'Btu/Btud', text: 'Btu/Btud'},
    {key: 'kwh/cfm', value: 'kwh/cfm', text: 'kwh/cfm'},
    {key: 'tons', value: 'tons', text: 'tons'},
    {key: 'amps', value: 'amps', text: 'amps'},
    {key: 'Mcf', value: 'Mcf', text: 'Mcf'},
    {key: 'psid', value: 'psid', text: 'psid'},
    {key: 'kw/mmbtuh', value: 'kw/mmbtuh', text: 'kW/MMBtuh'},
    {key: 'kw/gpm', value: 'kw/gpm', text: 'kW/gpm'},
  ];

  const energyMetricUnits = [
    {key: 'kW', value: 'kW', text: 'kW'},
    {key: 'Btu/hr', value: 'Btu/hr', text: 'Btu/hr'},
    {key: 'Mcf/hr', value: 'Mcf/hr', text: 'Mcf/hr'},
  ];

  const energyWasteUnits = [
    {key: 'kW', value: 'kW', text: 'kW'},
    {key: 'Btu', value: 'Btu', text: 'Btu'},
    {key: 'kBtu', value: 'kBtu', text: 'kBtu'},
    {key: 'MMBtu', value: 'MMBtu', text: 'MMBtu'},
  ];

  useEffect(() => {
    if (metricId > 0 && type !== '' && open) getReportMetric(metricId, type);
  }, [getReportMetric, metricId, type, open]);

  useEffect(() => {
    if (!reportMetric) return;

    setFormName(reportMetric.name ?? '');
    setFormIsEnergyMetric(reportMetric.isEnergyMetric);
    setFormUnit(reportMetric.units);

    if (type === 'ideal') {
      setFormIdealCurveFunction(reportMetric.code ?? '');
      setFormComparisonIsFunction(reportMetric.comparisonIsFunction);
      setFormComparisonCode(reportMetric.comparisonGenericPointId);
      setFormElectricConsumptionFunction(reportMetric.electricConsumptionFunction ?? '');
      setFormElectricConsumptionUnit(reportMetric.electricConsumptionUnit ?? '');
      setFormElectricWasteFunction(reportMetric.electricWasteFunction ?? '');
      setFormElectricWasteUnit(reportMetric.electricWasteUnit ?? '');
      setFormThermalConsumptionFunction(reportMetric.thermalConsumptionFunction ?? '');
      setFormThermalConsumptionUnit(reportMetric.thermalConsumptionUnit ?? '');
      setFormThermalWasteFunction(reportMetric.thermalWasteFunction ?? '');
      setFormThermalWasteUnit(reportMetric.thermalWasteUnit ?? '');
    }

    const points = (reportMetric.genericPointIds || []).map(x => x.key);
    setFormPoints(points);

    const pointDisplays = (reportMetric.genericPointIds || []).map(x => ({
      key: x.key,
      text: `${x.key} - ${x.value}`,
      value: x.key,
    }));
    setFormPointDisplays(pointDisplays);

    const functions = (reportMetric.functions || []).map(x => x.key);
    setFormFunctions(functions);

    const functionDisplays = (reportMetric.functions || []).map(x => ({
        key: x.key,
        text: x.value,
        value: x.key,
    }));
    setFormFunctionDisplays(functionDisplays);
  }, [
    type,
    setFormName,
    setFormIsEnergyMetric,
    setFormUnit,
    setFormIdealCurveFunction,
    setFormComparisonIsFunction,
    setFormComparisonCode,
    setFormElectricConsumptionFunction,
    setFormElectricConsumptionUnit,
    setFormElectricWasteFunction,
    setFormElectricWasteUnit,
    setFormThermalConsumptionFunction,
    setFormThermalConsumptionUnit,
    setFormThermalWasteFunction,
    setFormThermalWasteUnit,
    setFormPointDisplays,
    setFormPoints,
    reportMetric,
  ]);

  const saveMetric = useCallback(() => {
    const formData = {
      templateId: templateId,
      metricId: metricId,
      metricType: type,
      name: formName,
      isEnergyMetric: formIsEnergyMetric,
      units: formUnit,
      idealCurveFunction: formIdealCurveFunction,
      comparisonCode: formComparisonCode,
      comparisonIsFunction: formComparisonIsFunction,
      electricConsumptionFunction: formElectricConsumptionFunction,
      electricConsumptionUnit: formElectricConsumptionUnit,
      electricWasteFunction: formElectricWasteFunction,
      electricWasteUnit: formElectricWasteUnit,
      thermalConsumptionFunction: formThermalConsumptionFunction,
      thermalConsumptionUnit: formThermalConsumptionUnit,
      thermalWasteFunction: formThermalWasteFunction,
      thermalWasteUnit: formThermalWasteUnit,
      genericPoints: formPoints,
      functions: formFunctions
    };
    upsertReportTemplateMetric(formData);
    setOpen(false);
  }, [
    templateId,
    metricId,
    type,
    formName,
    formIsEnergyMetric,
    formUnit,
    formIdealCurveFunction,
    formComparisonCode,
    formComparisonIsFunction,
    formElectricConsumptionFunction,
    formElectricConsumptionUnit,
    formElectricWasteFunction,
    formElectricWasteUnit,
    formThermalConsumptionFunction,
    formThermalConsumptionUnit,
    formThermalWasteFunction,
    formThermalWasteUnit,
    formPoints,
    upsertReportTemplateMetric,
    setOpen,
    formFunctions
  ]);

  useEffect(() => {
    // clear form state when closing modal
    if (!open) {
      clearReportMetric();
      setFormName('');
      setFormIsEnergyMetric(false);
      setFormUnit('');
      setFormIdealCurveFunction('');
      setFormComparisonCode('');
      setFormComparisonIsFunction(false);
      setFormElectricConsumptionFunction('');
      setFormElectricConsumptionUnit('');
      setFormElectricWasteFunction('');
      setFormElectricWasteUnit('');
      setFormThermalConsumptionFunction('');
      setFormThermalConsumptionUnit('');
      setFormThermalWasteFunction('');
      setFormThermalWasteUnit('');
      setFormPoints([]);
      setFormPointDisplays([]);
      setFormFunctions([]);
      setFormFunctionDisplays([]);
    }
  }, [
    open,
    setFormName,
    setFormIsEnergyMetric,
    setFormUnit,
    setFormIdealCurveFunction,
    setFormComparisonCode,
    setFormComparisonIsFunction,
    setFormElectricConsumptionFunction,
    setFormElectricConsumptionUnit,
    setFormElectricWasteFunction,
    setFormElectricWasteUnit,
    setFormThermalConsumptionFunction,
    setFormThermalConsumptionUnit,
    setFormThermalWasteFunction,
    setFormThermalWasteUnit,
    setFormPoints,
    setFormPointDisplays,
    setFormFunctions,
    setFormFunctionDisplays,
  ]);

  const onRowClick = point => {
    if (formPoints.includes(point.id)) {
      setFormPoints(formPoints.filter(x => x !== point.id));
      setFormPointDisplays(formPointDisplays.filter(x => x.key !== point.id));
    } else {
      setFormPoints([...formPoints, point.id]);
      const pointDisplay = {
        key: point.id,
        text: `${point.id} - ${point.description}`,
        value: point.id,
      };
      setFormPointDisplays([...formPointDisplays, pointDisplay]);
    }
  };

    const onFunctionRowClick = func => {
        if (formFunctions.includes(func.functionId)) {
            setFormFunctions(formFunctions.filter(x => x !== func.functionId));
            setFormFunctionDisplays(formFunctionDisplays.filter(x => x.key !== func.functionId));
        } else {
            setFormFunctions([...formFunctions, func.functionId]);
            const functionDisplay = {
                key: func.functionId,
                text: func.name,
                value: func.functionId,
            };
            setFormFunctionDisplays([...formFunctionDisplays, functionDisplay]);
        }
    };

  return (
    <Modal size="large" onOpen={() => setOpen(true)} onClose={() => setOpen(false)} open={open}>
      <Modal.Header>Add / Edit Metric</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group>
            <Form.Input
              label="Name"
              control={Input}
              value={formName}
              required
              width={12}
              onChange={e => setFormName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              control={Dropdown}
              fluid
              selection
              clearable
              search
              options={[
                {key: 0, value: false, text: 'No'},
                {key: 1, value: true, text: 'Yes'},
              ]}
              value={formIsEnergyMetric}
              label={'Energy Metric'}
              onChange={(e, selection) => {
                setFormIsEnergyMetric(selection.value);
              }}
              required
              width={6}
              style={{height: '38px', color: 'black'}}
            />
            <Form.Input
              control={Dropdown}
              fluid
              selection
              clearable
              search
              options={formIsEnergyMetric ? energyMetricUnits : units}
              value={formUnit}
              label={'Unit'}
              onChange={(e, selection) => {
                setFormUnit(selection.value);
              }}
              required
              width={6}
              style={{height: '38px', color: 'black'}}
            />
          </Form.Group>
          {type === 'ideal' ? (
            <>
              <Form.Group>
                <Form.Input
                  control={Dropdown}
                  fluid
                  selection
                  clearable
                  search
                  required
                  options={functionList}
                  value={formIdealCurveFunction}
                  label={'Ideal Curve Function'}
                  onChange={(e, selection) => {
                    setFormIdealCurveFunction(selection.value);
                  }}
                  width={12}
                  style={{height: '38px', color: 'black'}}
                />
              </Form.Group>
              <Form.Group>
                <Form.Input
                  control={Dropdown}
                  fluid
                  selection
                  clearable
                  search
                  options={[
                    {key: 0, value: false, text: 'No'},
                    {key: 1, value: true, text: 'Yes'},
                  ]}
                  value={formComparisonIsFunction}
                  label={'Comparison Is Function'}
                  onChange={(e, selection) => {
                    setFormComparisonIsFunction(selection.value);
                  }}
                  required
                  width={6}
                  style={{height: '38px', color: 'black'}}
                />
                <Form.Input
                  control={Dropdown}
                  fluid
                  selection
                  clearable
                  search
                  required
                  options={formComparisonIsFunction ? functionList : pointList}
                  value={formComparisonCode}
                  label={'Comparison Point / Function'}
                  onChange={(e, selection) => {
                    setFormComparisonCode(selection.value);
                  }}
                  width={6}
                  style={{height: '38px', color: 'black'}}
                />
              </Form.Group>
              {!formIsEnergyMetric ? (
                <>
                  <h3>Energy Consumption Functions</h3>
                  <Form.Group>
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={functionList}
                      value={formElectricConsumptionFunction}
                      label={'Electrical Consumption Function'}
                      onChange={(e, selection) => {
                        setFormElectricConsumptionFunction(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={energyWasteUnits}
                      value={formElectricConsumptionUnit}
                      label={'Electrical Consumption Unit'}
                      onChange={(e, selection) => {
                        setFormElectricConsumptionUnit(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={functionList}
                      value={formThermalConsumptionFunction}
                      label={'Thermal Consumption Function'}
                      onChange={(e, selection) => {
                        setFormThermalConsumptionFunction(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={energyWasteUnits}
                      value={formThermalConsumptionUnit}
                      label={'Thermal Consumption Unit'}
                      onChange={(e, selection) => {
                        setFormThermalConsumptionUnit(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                  </Form.Group>
                  <h3>Energy Waste Functions</h3>
                  <Form.Group>
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={functionList}
                      value={formElectricWasteFunction}
                      label={'Electrical Waste Function'}
                      onChange={(e, selection) => {
                        setFormElectricWasteFunction(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={energyWasteUnits}
                      value={formElectricWasteUnit}
                      label={'Electrical Waste Unit'}
                      onChange={(e, selection) => {
                        setFormElectricWasteUnit(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={functionList}
                      value={formThermalWasteFunction}
                      label={'Thermal Waste Function'}
                      onChange={(e, selection) => {
                        setFormThermalWasteFunction(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                    <Form.Input
                      control={Dropdown}
                      fluid
                      selection
                      clearable
                      search
                      options={energyWasteUnits}
                      value={formThermalWasteUnit}
                      label={'Thermal Waste Unit'}
                      onChange={(e, selection) => {
                        setFormThermalWasteUnit(selection.value);
                      }}
                      width={6}
                      style={{height: '38px', color: 'black'}}
                    />
                  </Form.Group>
                </>
              ) : null}
            </>
          ) : (
            <>
              <Form.Group>
                <div className="field">
                  <label>Points</label>
                  <Dropdown
                    placeholder=""
                    fluid
                    width={6}
                    label={'Points'}
                    multiple
                    selection
                    clearable
                    icon={null}
                    value={formPoints}
                    open={false}
                    options={formPointDisplays}
                    onChange={(e, {value}) => {
                      setFormPoints(value);
                    }}
                  />
                </div>
              </Form.Group>
                {type === 'benchmark' && (
                    <Form.Group>
                        <div className="field" style={{marginTop: '1em'}}>
                            <label>Functions</label>
                            <Dropdown
                                placeholder=""
                                fluid
                                width={6}
                                label={'Functions'}
                                multiple
                                selection
                                clearable
                                icon={null}
                                value={formFunctions}
                                open={false}
                                options={formFunctionDisplays}
                                onChange={(e, {value}) => {
                                    setFormFunctions(value);
                                }}
                            />
                        </div>
                    </Form.Group>
                )}
              <div style={{marginTop: 40}}>
                <Tab
                  panes={[
                    {
                      menuItem: 'Energy Points',
                      render: () => (
                        <Tab.Pane>
                          <GenericPointTable
                            points={energyPoints}
                            pointPages={energyPointPages}
                            getPoints={getEnergyPoints}
                            selectedPoints={formPoints}
                            onRowClick={onRowClick}
                          />
                        </Tab.Pane>
                      ),
                    },
                    {
                      menuItem: 'Variable Points',
                      render: () => (
                        <Tab.Pane>
                          <GenericPointTable
                            points={variablePoints}
                            pointPages={variablePointPages}
                            getPoints={getVariablePoints}
                            selectedPoints={formPoints}
                            onRowClick={onRowClick}
                          />
                        </Tab.Pane>
                      ),
                    },
                    {
                      menuItem: 'Position Points',
                      render: () => (
                        <Tab.Pane>
                          <GenericPointTable
                            points={positionPoints}
                            pointPages={positionPointPages}
                            getPoints={getPositionPoints}
                            selectedPoints={formPoints}
                            onRowClick={onRowClick}
                          />
                        </Tab.Pane>
                      ),
                    },
                    {
                      menuItem: 'Binary Points',
                      render: () => (
                        <Tab.Pane>
                          <GenericPointTable
                            points={binaryPoints}
                            pointPages={binaryPointPages}
                            getPoints={getBinaryPoints}
                            selectedPoints={formPoints}
                            onRowClick={onRowClick}
                          />
                        </Tab.Pane>
                      ),
                    },
                    {
                      menuItem: 'Numeric Points',
                      render: () => (
                        <Tab.Pane>
                          <GenericPointTable
                            points={numericPoints}
                            pointPages={numericPointPages}
                            getPoints={getNumericPoints}
                            selectedPoints={formPoints}
                            onRowClick={onRowClick}
                          />
                        </Tab.Pane>
                      ),
                    },
                  type === 'benchmark' && {
                      menuItem: 'Functions',
                      render: () => (
                        <Tab.Pane>
                          <FunctionsTable
                            functions={functionTableList}
                            selectedFunctions={formFunctions}
                            onRowClick={onFunctionRowClick}
                          />
                        </Tab.Pane>
                      ),
                    }
                  ]}
                />
              </div>
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          content="Save"
          labelPosition="right"
          icon="checkmark"
          onClick={saveMetric}
          positive
        />
      </Modal.Actions>
    </Modal>
  );
};

export const ReportTemplates = () => {
  const {
    reportTemplate,
    getReportTemplate,
    reportTemplates,
    getReportTemplates,
    deleteReportTemplate,
    deleteReportTemplateMetric,
    updateReportTemplateMetricIndex,
    createNewReportTemplate,
    selectedReportTemplate,
    setSelectedReportTemplate,
    selectedReportTemplateName,
    selectedReportTemplateIsApiOnly,
    updateReportTemplate,
  } = useReport();

  const [metricModalOpen, setMetricModalOpen] = useState(false);
  const [openModalId, setOpenModalId] = useState(0);
  const [openModalType, setOpenModalType] = useState('');
  const [deleteMetricConfirmationModalOpen, setDeleteMetricConfirmationModalOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState({id: null, type: null, name: null});
  const [newTemplateModalOpen, setNewTemplateModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateIsApiOnly, setNewTemplateIsApiOnly] = useState(false);
  const [updateTemplateModalOpen, setUpdateTemplateModalOpen] = useState(false);
  const [updateTemplateName, setUpdateTemplateName] = useState('');
  const [updateTemplateIsApiOnly, setUpdateTemplateIsApiOnly] = useState(false);
  const [deleteTemplateConfirmationModalOpen, setDeleteTemplateConfirmationModalOpen] =
    useState(false);

  useEffect(() => {
    getReportTemplates();
  }, [getReportTemplates, setSelectedReportTemplate]);

  useEffect(() => {
    if (selectedReportTemplate) getReportTemplate(selectedReportTemplate);
  }, [getReportTemplate, selectedReportTemplate]);

  const openModal = useCallback(
    (id, type) => {
      setMetricModalOpen(true);
      setOpenModalId(id);
      setOpenModalType(type);
    },
    [setMetricModalOpen, setOpenModalId, setOpenModalType]
  );

  const openDeleteConfirmationModal = useCallback(
    (id, type, name) => {
      setDeleteMetricConfirmationModalOpen(true);
      setMetricToDelete({id, type, name});
    },
    [setDeleteMetricConfirmationModalOpen, setMetricToDelete]
  );

  const yesDeleteMetric = useCallback(() => {
    deleteReportTemplateMetric(metricToDelete.id, metricToDelete.type);
    setDeleteMetricConfirmationModalOpen(false);
    setMetricToDelete({id: null, type: null, name: null});
  }, [
    deleteReportTemplateMetric,
    metricToDelete,
    setDeleteMetricConfirmationModalOpen,
    setMetricToDelete,
  ]);

  const noDeleteMetric = useCallback(() => {
    setDeleteMetricConfirmationModalOpen(false);
    setMetricToDelete({id: null, type: null, name: null});
  }, [setDeleteMetricConfirmationModalOpen, setMetricToDelete]);

  const openNewTemplateModal = useCallback(() => {
    setNewTemplateModalOpen(true);
    setNewTemplateName('');
  }, [setNewTemplateModalOpen, setNewTemplateName]);

  const createNewTemplate = useCallback(() => {
    createNewReportTemplate(newTemplateName, newTemplateIsApiOnly);
    setNewTemplateModalOpen(false);
  }, [createNewReportTemplate, newTemplateName, newTemplateIsApiOnly, setNewTemplateModalOpen]);

  const cancelNewTemplate = useCallback(() => {
    setNewTemplateModalOpen(false);
    setNewTemplateName('');
  }, [setNewTemplateModalOpen, setNewTemplateName]);

  const openUpdateTemplate = useCallback(() => {
    setUpdateTemplateModalOpen(true);
    setUpdateTemplateName(selectedReportTemplateName);
    setUpdateTemplateIsApiOnly(selectedReportTemplateIsApiOnly);
  }, [setUpdateTemplateModalOpen, selectedReportTemplateName, selectedReportTemplateIsApiOnly]);

  const updateTemplate = useCallback(() => {
    updateReportTemplate(selectedReportTemplate, updateTemplateName, updateTemplateIsApiOnly);
    setUpdateTemplateModalOpen(false);
  }, [
    updateReportTemplate,
    selectedReportTemplate,
    updateTemplateName,
    updateTemplateIsApiOnly,
    setUpdateTemplateModalOpen,
  ]);

  const cancelUpdateTemplate = useCallback(() => {
    setUpdateTemplateModalOpen(false);
    setUpdateTemplateName('');
    setUpdateTemplateIsApiOnly(false);
  }, [setUpdateTemplateModalOpen, setUpdateTemplateName, setUpdateTemplateIsApiOnly]);

  const yesDeleteTemplate = useCallback(() => {
    deleteReportTemplate(selectedReportTemplate);
    setDeleteTemplateConfirmationModalOpen(false);
  }, [deleteReportTemplate, selectedReportTemplate, setDeleteTemplateConfirmationModalOpen]);

  const noDeleteTemplate = useCallback(() => {
    setDeleteTemplateConfirmationModalOpen(false);
  }, [setDeleteTemplateConfirmationModalOpen]);

  const updateMetricIndex = useCallback(
    (id, type, moveUp) => {
      updateReportTemplateMetricIndex(id, type, moveUp);
    },
    [updateReportTemplateMetricIndex]
  );

  return (
    <div>
      <MetricModal
        open={metricModalOpen}
        setOpen={setMetricModalOpen}
        templateId={selectedReportTemplate}
        metricId={openModalId}
        type={openModalType}
      />
      <Modal
        onClose={() => cancelNewTemplate()}
        onOpen={() => setNewTemplateModalOpen(true)}
        open={newTemplateModalOpen}
        size="small"
      >
        <Header icon>Add New Template</Header>
        <Modal.Content>
          <Form>
            <Form.Group>
              <Form.Input
                label="Name"
                control={Input}
                value={newTemplateName}
                required
                width={12}
                onChange={e => setNewTemplateName(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Input
                label="API Only"
                control={Checkbox}
                checked={newTemplateIsApiOnly}
                toggle
                required
                width={12}
                onChange={(e, data) => setNewTemplateIsApiOnly(data.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="black" onClick={() => cancelNewTemplate()}>
            Cancel
          </Button>
          <Button color="green" onClick={() => createNewTemplate()}>
            <Icon name="checkmark" /> Create
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        onClose={() => cancelUpdateTemplate()}
        onOpen={() => setUpdateTemplateModalOpen(true)}
        open={updateTemplateModalOpen}
        size="small"
      >
        <Header icon>Update Template</Header>
        <Modal.Content>
          <Form>
            <Form.Group>
              <Form.Input
                label="Name"
                control={Input}
                value={updateTemplateName}
                required
                width={12}
                onChange={e => setUpdateTemplateName(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Input
                label="API Only"
                control={Checkbox}
                checked={updateTemplateIsApiOnly}
                toggle
                required
                width={12}
                onChange={(e, data) => setUpdateTemplateIsApiOnly(data.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="black" onClick={() => cancelUpdateTemplate()}>
            Cancel
          </Button>
          <Button color="green" onClick={() => updateTemplate()}>
            <Icon name="checkmark" /> Save
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        basic
        onClose={() => setDeleteTemplateConfirmationModalOpen(false)}
        onOpen={() => setDeleteTemplateConfirmationModalOpen(true)}
        open={deleteTemplateConfirmationModalOpen}
        size="small"
      >
        <Header icon>
          <Icon name="trash" />
          Delete Template
        </Header>
        <Modal.Content>
          <p>Are you sure you would like to delete the template "{selectedReportTemplateName}"?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="red" inverted onClick={() => noDeleteTemplate()}>
            <Icon name="remove" /> No
          </Button>
          <Button color="green" inverted onClick={() => yesDeleteTemplate()}>
            <Icon name="checkmark" /> Yes
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        basic
        onClose={() => setDeleteMetricConfirmationModalOpen(false)}
        onOpen={() => setDeleteMetricConfirmationModalOpen(true)}
        open={deleteMetricConfirmationModalOpen}
        size="small"
      >
        <Header icon>
          <Icon name="trash" />
          Delete Metric
        </Header>
        <Modal.Content>
          <p>Are you sure you would like to delete the metric "{metricToDelete.name}"?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="red" inverted onClick={() => noDeleteMetric()}>
            <Icon name="remove" /> No
          </Button>
          <Button color="green" inverted onClick={() => yesDeleteMetric()}>
            <Icon name="checkmark" /> Yes
          </Button>
        </Modal.Actions>
      </Modal>
      <Form>
        <Form.Group>
          <Form.Field
            label={'Report Template'}
            control={Dropdown}
            fluid
            selection
            options={reportTemplates.map(x => {
              return {
                key: x?.id,
                text: x?.name,
                value: x?.id,
              };
            })}
            value={selectedReportTemplate}
            style={{height: '38px'}}
            onChange={(e, selected) => {
              setSelectedReportTemplate(selected.value);
            }}
            width={10}
            search
          />
          <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end'}}>
            <Button color="blue" onClick={() => openUpdateTemplate()}>
              <Icon name="pencil" /> Edit Template
            </Button>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end'}}>
            <Button color="red" onClick={() => setDeleteTemplateConfirmationModalOpen(true)}>
              <Icon name="trash" /> Delete Template
            </Button>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end'}}>
            <Button color="green" onClick={() => openNewTemplateModal()}>
              <Icon name="plus" /> New Template
            </Button>
          </div>
        </Form.Group>
      </Form>

      {reportTemplate && (
        <div style={{ minWidth: 300}}>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell colSpan="5">
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <h3 style={{marginBottom: 0}}>Ideal Metrics</h3>
                    <div>
                      <Button onClick={() => openModal(null, 'ideal')}>
                        <Icon name={'plus'} />
                        Add Metric
                      </Button>
                    </div>
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>Index</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell collapsing>Energy Metric</Table.HeaderCell>
                <Table.HeaderCell collapsing>Units</Table.HeaderCell>
                <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {(reportTemplate.idealMetrics ?? []).map(m => (
                <Table.Row key={m.id}>
                  <Table.Cell collapsing>{m.index}</Table.Cell>
                  <Table.Cell>{m.name}</Table.Cell>
                  <Table.Cell collapsing>{m.isEnergyMetric ? 'Yes' : 'No'}</Table.Cell>
                  <Table.Cell collapsing>{m.units}</Table.Cell>
                  <Table.Cell collapsing>
                    <Button onClick={() => openModal(m.id, 'ideal')}>
                      <Icon name="pencil" />
                      Edit
                    </Button>
                    <Button onClick={() => openDeleteConfirmationModal(m.id, 'ideal', m.name)}>
                      <Icon name="trash" />
                      Delete
                    </Button>
                    <Button onClick={() => updateMetricIndex(m.id, 'ideal', true)}>
                      <Icon name="angle up" />
                      Move Up
                    </Button>
                    <Button onClick={() => updateMetricIndex(m.id, 'ideal', false)}>
                      <Icon name="angle down" />
                      Move Down
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell colSpan="5">
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <h3 style={{marginBottom: 0}}>Benchmark Metrics</h3>
                    <div>
                      <Button onClick={() => openModal(null, 'benchmark')}>
                        <Icon name={'plus'} />
                        Add Metric
                      </Button>
                    </div>
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>Index</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell collapsing>Energy Metric</Table.HeaderCell>
                <Table.HeaderCell collapsing>Units</Table.HeaderCell>
                <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {(reportTemplate.benchmarkMetrics ?? []).map(m => (
                <Table.Row key={m.id}>
                  <Table.Cell collapsing>{m.index}</Table.Cell>
                  <Table.Cell>{m.name}</Table.Cell>
                  <Table.Cell collapsing>{m.isEnergyMetric ? 'Yes' : 'No'}</Table.Cell>
                  <Table.Cell collapsing>{m.units}</Table.Cell>
                  <Table.Cell collapsing>
                    <Button onClick={() => openModal(m.id, 'benchmark')}>
                      <Icon name="pencil" />
                      Edit
                    </Button>
                    <Button onClick={() => openDeleteConfirmationModal(m.id, 'benchmark', m.name)}>
                      <Icon name="trash" />
                      Delete
                    </Button>
                    <Button onClick={() => updateMetricIndex(m.id, 'benchmark', true)}>
                      <Icon name="angle up" />
                      Move Up
                    </Button>
                    <Button onClick={() => updateMetricIndex(m.id, 'benchmark', false)}>
                      <Icon name="angle down" />
                      Move Down
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell colSpan="5">
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <h3 style={{marginBottom: 0}}>Point Metrics</h3>
                    <div>
                      <Button onClick={() => openModal(null, 'point')}>
                        <Icon name={'plus'} />
                        Add Metric
                      </Button>
                    </div>
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>Index</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell collapsing>Energy Metric</Table.HeaderCell>
                <Table.HeaderCell collapsing>Units</Table.HeaderCell>
                <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {(reportTemplate.pointMetrics ?? []).map(m => (
                <Table.Row key={m.id}>
                  <Table.Cell collapsing>{m.index}</Table.Cell>
                  <Table.Cell>{m.name}</Table.Cell>
                  <Table.Cell collapsing>{m.isEnergyMetric ? 'Yes' : 'No'}</Table.Cell>
                  <Table.Cell collapsing>{m.units}</Table.Cell>
                  <Table.Cell collapsing>
                    <Button onClick={() => openModal(m.id, 'point')}>
                      <Icon name="pencil" />
                      Edit
                    </Button>
                    <Button onClick={() => openDeleteConfirmationModal(m.id, 'point', m.name)}>
                      <Icon name="trash" />
                      Delete
                    </Button>
                    <Button onClick={() => updateMetricIndex(m.id, 'point', true)}>
                      <Icon name="angle up" />
                      Move Up
                    </Button>
                    <Button onClick={() => updateMetricIndex(m.id, 'point', false)}>
                      <Icon name="angle down" />
                      Move Down
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};
